import { useEffect, useRef, useState } from 'react';
import { T, useGT } from 'gt-react';
import { format } from 'date-fns';
import { TrashIcon } from '@phosphor-icons/react';
import { parseDateOnly } from '@/utils/dateUtils';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { DatePicker } from '@/components/common/date-pickers/date-picker';
import { DecimalInput } from '@/components/common/DecimalInput';
import { CustomersAutocomplete } from '@/components/customer/CustomersAutocomplete';
import {
  useCreateRemittance,
  useUpdateRemittance,
} from '@/api/rpc/hooks/accounts-receivable/useArRemittances';
import { useToast } from 'src/context/ToastContext';
import type { RemittanceDetail, RemittanceLineItemKind } from '@erp/rpc';

interface DraftLineItem {
  rawInvoiceRef: string;
  statedAmount: string | null;
  kind: RemittanceLineItemKind;
}

function emptyLineItem(): DraftLineItem {
  return { rawInvoiceRef: '', statedAmount: null, kind: 'invoice_payment' };
}

interface RemittanceFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Provided in edit mode (header fields only); omit for create. */
  remittance?: RemittanceDetail;
  onSaved?: (remittanceId: string) => void;
}

export function RemittanceFormDialog({
  open,
  onOpenChange,
  remittance,
  onSaved,
}: RemittanceFormDialogProps) {
  const gt = useGT();
  const { showToast } = useToast();
  const createMutation = useCreateRemittance();
  const updateMutation = useUpdateRemittance();
  const isEdit = Boolean(remittance);
  const isPending = createMutation.isPending || updateMutation.isPending;

  const [customerId, setCustomerId] = useState<string | undefined>(undefined);
  const [statedCustomerName, setStatedCustomerName] = useState('');
  const [statedTotalAmount, setStatedTotalAmount] = useState<string | null>(
    null,
  );
  const [statedPaymentDate, setStatedPaymentDate] = useState<string | null>(
    null,
  );
  const [statedPaymentMethod, setStatedPaymentMethod] = useState('');
  const [statedReferenceNo, setStatedReferenceNo] = useState('');
  const [lineItems, setLineItems] = useState<DraftLineItem[]>([
    emptyLineItem(),
  ]);

  // Re-seed only on open or when targeting a different remittance; keyed on id
  // via a ref so a background refetch can't wipe in-progress edits.
  const initedKey = useRef<string | null>(null);
  useEffect(() => {
    if (!open) {
      initedKey.current = null;
      return;
    }
    const key = remittance?.id ?? '__new__';
    if (initedKey.current === key) return;
    initedKey.current = key;
    setCustomerId(remittance?.matchedCustomer?.id ?? undefined);
    setStatedCustomerName(remittance?.statedCustomerName ?? '');
    setStatedTotalAmount(remittance?.statedTotalAmount ?? null);
    setStatedPaymentDate(remittance?.statedPaymentDate ?? null);
    setStatedPaymentMethod(remittance?.statedPaymentMethod ?? '');
    setStatedReferenceNo(remittance?.statedReferenceNo ?? '');
    setLineItems([emptyLineItem()]);
  }, [open, remittance]);

  const canSubmit = !isPending;

  function updateLine(index: number, patch: Partial<DraftLineItem>) {
    setLineItems((prev) =>
      prev.map((li, i) => (i === index ? { ...li, ...patch } : li)),
    );
  }

  async function handleSubmit() {
    if (!canSubmit) return;
    const header = {
      statedCustomerName: statedCustomerName.trim() || null,
      statedTotalAmount,
      statedPaymentDate,
      statedPaymentMethod: statedPaymentMethod.trim() || null,
      statedReferenceNo: statedReferenceNo.trim() || null,
    };
    try {
      const result =
        isEdit && remittance
          ? await updateMutation.mutateAsync({ id: remittance.id, ...header })
          : await createMutation.mutateAsync({
              customerId: customerId ?? null,
              ...header,
              lineItems: lineItems
                .filter((li) => li.rawInvoiceRef.trim().length > 0)
                .map((li) => ({
                  rawInvoiceRef: li.rawInvoiceRef.trim(),
                  statedAmount: li.statedAmount,
                  kind: li.kind,
                })),
            });
      showToast({
        title: isEdit
          ? gt('Remittance updated', {
              context: 'toast title, remittance updated',
            })
          : gt('Remittance created', {
              context: 'toast title, remittance created',
            }),
        color: 'success',
        icon: 'check',
      });
      onSaved?.(result.remittance.id);
      onOpenChange(false);
    } catch (err) {
      showToast({
        title: gt('Save failed', {
          context: 'toast title, remittance save failed',
        }),
        message: err instanceof Error ? err.message : '',
        color: 'error',
        icon: 'warning',
      });
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {isEdit ? (
              <T context="dialog title, edit a remittance">Edit remittance</T>
            ) : (
              <T context="dialog title, record a new remittance">
                Record a remittance
              </T>
            )}
          </DialogTitle>
          <DialogDescription>
            <T context="dialog description, manual remittance entry">
              Enter a remittance advice a customer sent outside the normal email
              flow. Line items can be matched to invoices after saving.
            </T>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {!isEdit && (
            <div className="space-y-2">
              <Label>
                <T context="form label, remittance customer">
                  Customer (optional)
                </T>
              </Label>
              <CustomersAutocomplete
                selectedCustomerId={customerId}
                onCustomerSelect={setCustomerId}
                allowClear
              />
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label htmlFor="rem-name">
                <T context="form label, stated customer name">Stated name</T>
              </Label>
              <Input
                id="rem-name"
                value={statedCustomerName}
                onChange={(e) => setStatedCustomerName(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="rem-amount">
                <T context="form label, remittance total amount">Total</T>
              </Label>
              <DecimalInput
                id="rem-amount"
                value={statedTotalAmount}
                onChange={setStatedTotalAmount}
              />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-2">
              <Label htmlFor="rem-date">
                <T context="form label, remittance payment date">Date</T>
              </Label>
              <DatePicker
                value={
                  statedPaymentDate
                    ? (parseDateOnly(statedPaymentDate) ?? undefined)
                    : undefined
                }
                onChange={(d) =>
                  setStatedPaymentDate(d ? format(d, 'yyyy-MM-dd') : null)
                }
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="rem-method">
                <T context="form label, remittance payment method">Method</T>
              </Label>
              <Input
                id="rem-method"
                value={statedPaymentMethod}
                onChange={(e) => setStatedPaymentMethod(e.target.value)}
                placeholder={gt('check, wire...', {
                  context: 'placeholder, payment method',
                })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="rem-ref">
                <T context="form label, remittance reference">Reference #</T>
              </Label>
              <Input
                id="rem-ref"
                value={statedReferenceNo}
                onChange={(e) => setStatedReferenceNo(e.target.value)}
              />
            </div>
          </div>

          {!isEdit && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>
                  <T context="form label, remittance line items">Line items</T>
                </Label>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={() =>
                    setLineItems((prev) => [...prev, emptyLineItem()])
                  }
                >
                  <T context="button, add a remittance line item">Add line</T>
                </Button>
              </div>
              <div className="space-y-2">
                {lineItems.map((li, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <Input
                      value={li.rawInvoiceRef}
                      onChange={(e) =>
                        updateLine(index, { rawInvoiceRef: e.target.value })
                      }
                      placeholder={gt('Invoice ref', {
                        context:
                          'placeholder, remittance line invoice reference',
                      })}
                      className="flex-1"
                    />
                    <DecimalInput
                      value={li.statedAmount}
                      onChange={(v) => updateLine(index, { statedAmount: v })}
                      className="w-28"
                    />
                    <Select
                      value={li.kind}
                      onValueChange={(v) =>
                        updateLine(index, {
                          kind: v as RemittanceLineItemKind,
                        })
                      }
                    >
                      <SelectTrigger className="w-32">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="invoice_payment">
                          {gt('Payment', {
                            context: 'remittance line kind, invoice payment',
                          })}
                        </SelectItem>
                        <SelectItem value="deduction">
                          {gt('Deduction', {
                            context: 'remittance line kind, deduction',
                          })}
                        </SelectItem>
                      </SelectContent>
                    </Select>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="size-8 shrink-0 text-muted-foreground hover:text-destructive"
                      disabled={lineItems.length === 1}
                      onClick={() =>
                        setLineItems((prev) =>
                          prev.filter((_, i) => i !== index),
                        )
                      }
                      aria-label={gt('Remove line', {
                        context: 'a11y label, remove remittance line item row',
                      })}
                    >
                      <TrashIcon className="size-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isPending}
          >
            <T context="button label, cancel dialog">Cancel</T>
          </Button>
          <Button onClick={handleSubmit} disabled={!canSubmit}>
            {isEdit ? (
              <T context="button label, save remittance edits">Save</T>
            ) : (
              <T context="button label, create remittance">Create</T>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
